import { isFeatureEnabledForUser } from '@carrierbridge/shared';
import { useTranslation } from 'react-i18next';
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Skeleton } from '@/components/ui/Skeleton';
import { LanguageSelect } from '@/components/layout/LanguageSelect';
import { AuthProvider, RequireRole, RequireFeature, useAuthContext } from '@/contexts/AuthContext';
import { LISTS_VIEW, VIEW_PARAM } from '@/lib/carrier-lists';
// Imports statiques volontaires : LoginPage (page d'atterrissage la plus
// fréquente — évite un double aller-retour réseau au login) et QuotePage
// (accueil post-login). Tout le reste est chargé à la demande (React.lazy).
import { LoginPage } from '@/pages/auth/LoginPage';
import { QuotePage } from '@/pages/QuotePage';

// Pages publiques hors Layout (couvertes par le <Suspense> racine ci-dessous).
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const AcceptInvitePage = lazy(() => import('@/pages/auth/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })));
const SsoCallbackPage = lazy(() => import('@/pages/auth/SsoCallbackPage').then((m) => ({ default: m.SsoCallbackPage })));
const SsoStartPage = lazy(() => import('@/pages/auth/SsoStartPage').then((m) => ({ default: m.SsoStartPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const SuiviPublicPage = lazy(() => import('@/pages/operations/SuiviPublicPage').then((m) => ({ default: m.SuiviPublicPage })));
const CotationPublicPage = lazy(() => import('@/pages/operations/CotationPublicPage').then((m) => ({ default: m.CotationPublicPage })));
const VigilancePortalPage = lazy(() => import('@/pages/vigilance').then((m) => ({ default: m.VigilancePortalPage })));
const AccessLinkRouter = lazy(() => import('@/pages/carrier-access/AccessLinkRouter').then((m) => ({ default: m.AccessLinkRouter })));
const TenderPortalPage = lazy(() => import('@/pages/tenders/portal/TenderPortalPage').then((m) => ({ default: m.TenderPortalPage })));
const CarrierSpacePage = lazy(() => import('@/pages/carrier-space/CarrierSpacePage').then((m) => ({ default: m.CarrierSpacePage })));
const CarrierDocumentsPage = lazy(() => import('@/pages/carrier-space/CarrierDocumentsPage').then((m) => ({ default: m.CarrierDocumentsPage })));

// Pages protégées, rendues dans le Layout (dont le <Suspense> autour de
// l'Outlet affiche des Skeletons pendant le chargement du chunk).
const QuoteResponsesListPage = lazy(() => import('@/pages/quote-response/QuoteResponsesListPage').then((m) => ({ default: m.QuoteResponsesListPage })));
const QuoteResponseDetailPage = lazy(() => import('@/pages/quote-response/QuoteResponseDetailPage').then((m) => ({ default: m.QuoteResponseDetailPage })));
const RobertPage = lazy(() => import('@/pages/RobertPage').then((m) => ({ default: m.RobertPage })));
const FuelIndicesInfoPage = lazy(() => import('@/pages/FuelIndicesInfoPage').then((m) => ({ default: m.FuelIndicesInfoPage })));
const GridImportDetailPage = lazy(() => import('@/pages/GridImportPage').then((m) => ({ default: m.GridImportDetailPage })));
const ImportsTabPage = lazy(() => import('@/pages/ImportsTabPage').then((m) => ({ default: m.ImportsTabPage })));
const CarriersPage = lazy(() => import('@/pages/CarriersPage').then((m) => ({ default: m.CarriersPage })));
const CarriersRoute = lazy(() => import('@/pages/carriers/CarriersRoute').then((m) => ({ default: m.CarriersRoute })));
const CarrierDetailPage = lazy(() => import('@/pages/CarrierDetailPage').then((m) => ({ default: m.CarrierDetailPage })));
const SuperAdminPage = lazy(() => import('@/pages/superadmin/SuperAdminPage').then((m) => ({ default: m.SuperAdminPage })));
const AncillaryImportPage = lazy(() => import('@/pages/ancillary-imports/AncillaryImportPage').then((m) => ({ default: m.AncillaryImportPage })));
const ZoneEditorPage = lazy(() => import('@/pages/zones').then((m) => ({ default: m.ZoneEditorPage })));
const DatasetsListPage = lazy(() => import('@/pages/datasets').then((m) => ({ default: m.DatasetsListPage })));
const DatasetImportPage = lazy(() => import('@/pages/datasets').then((m) => ({ default: m.DatasetImportPage })));
const DatasetDetailPage = lazy(() => import('@/pages/datasets').then((m) => ({ default: m.DatasetDetailPage })));
const PricingGridImportWizardPage = lazy(() => import('@/pages/pricing-grid-imports').then((m) => ({ default: m.PricingGridImportWizardPage })));
const DataConsolePage = lazy(() => import('@/pages/data/DataConsolePage').then((m) => ({ default: m.DataConsolePage })));
const BudgetAnalysisPage = lazy(() => import('@/pages/analysis').then((m) => ({ default: m.BudgetAnalysisPage })));
const SettingsLayout = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsLayout })));
const SettingsGeneralPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsGeneralPage })));
const SettingsTeamPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsTeamPage })));
const SettingsBrandingPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsBrandingPage })));
const SettingsEmailsPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsEmailsPage })));
const SettingsActivityPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsActivityPage })));
const SettingsReferentialPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsReferentialPage })));
const SettingsDataPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsDataPage })));
const SettingsEnergyPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsEnergyPage })));
const SettingsAccountPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsAccountPage })));
const SettingsZonesPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsZonesPage })));
const SettingsGridTemplatesPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsGridTemplatesPage })));
const SettingsMaturityPage = lazy(() => import('@/pages/settings').then((m) => ({ default: m.SettingsMaturityPage })));
const VigilanceComplianceListPage = lazy(() => import('@/pages/vigilance').then((m) => ({ default: m.VigilanceComplianceListPage })));
const VigilanceSpaceDetailPage = lazy(() => import('@/pages/vigilance').then((m) => ({ default: m.VigilanceSpaceDetailPage })));
const TendersListPage = lazy(() => import('@/pages/tenders').then((m) => ({ default: m.TendersListPage })));
const TenderDetailPage = lazy(() => import('@/pages/tenders').then((m) => ({ default: m.TenderDetailPage })));
const TenderProposalDetailPage = lazy(() => import('@/pages/tenders').then((m) => ({ default: m.TenderProposalDetailPage })));
const CarrierPoolDetailPage = lazy(() => import('@/pages/tenders').then((m) => ({ default: m.CarrierPoolDetailPage })));
const SourcingPage = lazy(() => import('@/pages/tenders').then((m) => ({ default: m.SourcingPage })));
const AutoTenderDetailPage = lazy(() => import('@/pages/auto-tenders/AutoTenderDetailPage').then((m) => ({ default: m.AutoTenderDetailPage })));
const AutoReferencePage = lazy(() => import('@/pages/auto-tenders/AutoReferencePage').then((m) => ({ default: m.AutoReferencePage })));
const OperationsConsolePage = lazy(() => import('@/pages/operations/OperationsConsolePage').then((m) => ({ default: m.OperationsConsolePage })));
const BaseOperationsPage = lazy(() => import('@/pages/operations/BaseOperationsPage').then((m) => ({ default: m.BaseOperationsPage })));
const OperationsCarriersPage = lazy(() => import('@/pages/operations/OperationsCarriersPage').then((m) => ({ default: m.OperationsCarriersPage })));
const OrderingPartiesPage = lazy(() => import('@/pages/ordering-parties/OrderingPartiesPage').then((m) => ({ default: m.OrderingPartiesPage })));

/**
 * Première page des Réglages selon le rôle : « Général » exige editor, un
 * compte en lecture seule n'y verrait qu'un 403 — il arrive sur les indices
 * énergie, seule section de son espace de travail.
 */
function SettingsIndexRedirect() {
  const { user } = useAuthContext();
  const canManage = ['admin', 'editor'].includes(user.role);
  return <Navigate to={canManage ? '/settings/general' : '/settings/energy'} replace />;
}

/**
 * L'ancienne page « Vivier » est devenue la vue Listes de la page Transporteurs : les favoris et la
 * palette ⌘K y arrivent toujours, avec leur recherche et leur liste choisie.
 */
function CarrierPoolRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set(VIEW_PARAM, LISTS_VIEW);
  return <Navigate to={{ pathname: '/carriers', search: `?${params.toString()}` }} replace />;
}

/**
 * « Répondre à une cotation » était un onglet du Devis (`/quote?tab=response`) ; c'est désormais une page à part,
 * `/quote-response`. Les liens déjà partagés y mènent toujours. La redirection passe avant le contrôle du module
 * Devis : répondre à une cotation est une option à part, qu'une société peut avoir sans le Devis.
 */
function QuoteEntry() {
  const { search } = useLocation();
  if (new URLSearchParams(search).get('tab') === 'response') return <Navigate to="/quote-response" replace />;
  return <RequireFeature feature="quote"><QuotePage /></RequireFeature>;
}

function HomeRedirect() {
  const { user } = useAuthContext();
  if (user.role === 'superadmin') return <Navigate to="/superadmin" replace />;
  // Tarifer est une option : sans lui, on atterrit sur la première page ouverte.
  const home = isFeatureEnabledForUser(user, 'quote')
    ? '/quote'
    : isFeatureEnabledForUser(user, 'operations')
      ? '/operations'
      : '/carriers';
  return <Navigate to={home} replace />;
}

/**
 * Attente d'une page publique (portail transporteur, suivi, connexion
 * secondaire) : un écran blanc pendant le téléchargement du morceau de code se
 * lisait comme une page cassée. Un squelette centré dit que ça charge.
 */
function PublicFallback() {
  return (
    <div className="mx-auto max-w-3xl space-y-4 px-4 py-10" role="status" aria-busy="true">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

/** Coque des pages publiques : contenu + sélecteur de langue en coin. */
function PublicShell() {
  return (
    <>
      <Outlet />
      <div className="fixed right-3 top-3 z-30 rounded border border-border bg-surface/90 px-1.5 shadow-sm backdrop-blur">
        <LanguageSelect />
      </div>
    </>
  );
}

export default function App() {
  return (
    // Suspense racine : ne sert qu'aux routes publiques hors Layout (register,
    // invitation, reset password, suivi/cotation publics, 404 standalone).
    // Les pages protégées sont capturées par le Suspense du Layout (plus
    // proche), qui affiche des Skeletons dans la zone de contenu.
    <Suspense fallback={<PublicFallback />}>
      <Routes>
        {/* Pages publiques : même coque, avec le sélecteur de langue (un
            transporteur étranger ouvre un lien reçu par e-mail sans compte). */}
        <Route element={<PublicShell />}>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/invite/:token" element={<AcceptInvitePage />} />
          {/* Connexion via le portail Pool4ward (SSO) : la tuile ouvre /sso/start, qui lance
              l'autorisation avec un state gardé dans ce navigateur ; le portail revient sur
              /sso/callback avec le code et ce state. */}
          <Route path="/sso/start" element={<SsoStartPage />} />
          <Route path="/sso/callback" element={<SsoCallbackPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          {/* Suivi transporteur public (canal LIEN tokenisé, sans compte) */}
          <Route path="/operations/suivi/:token" element={<SuiviPublicPage />} />
          {/* Portail de cotation spot public (token, sans compte) */}
          <Route path="/operations/cotation/:token" element={<CotationPublicPage />} />
          {/* Portail transporteur de vigilance documentaire (token, sans compte) */}
          <Route path="/vigilance/:token" element={<VigilancePortalPage />} />

          {/* Portail d'appel d'offres transporteur (token, sans compte) */}
          <Route path="/tenders/proposition/:token" element={<TenderPortalPage />} />

          {/* Espace transporteur : compte facultatif, jamais un prérequis */}
          <Route path="/t/espace" element={<CarrierSpacePage />} />
          <Route path="/t/espace/documents/:orgId" element={<CarrierDocumentsPage />} />

          {/* Point d'entrée unique des liens transporteur : le serveur dit ce que
              le token ouvre. Les routes ci-dessus restent servies telles quelles —
              un lien déjà envoyé ne cesse jamais de fonctionner. */}
          <Route path="/t/:token" element={<AccessLinkRouter />} />
        </Route>

        {/* Protected routes */}
        <Route
          element={
            <AuthProvider>
              <Layout />
            </AuthProvider>
          }
        >
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/quote" element={<QuoteEntry />} />
          {/* Répondre à une cotation — module optionnel `quoteResponse` + droit de lire les prix d'achat (admin/editor) */}
          <Route path="/quote-response" element={<RequireFeature feature="quoteResponse"><RequireRole roles={['admin', 'editor']}><QuoteResponsesListPage /></RequireRole></RequireFeature>} />
          <Route path="/quote-response/:id" element={<RequireFeature feature="quoteResponse"><RequireRole roles={['admin', 'editor']}><QuoteResponseDetailPage /></RequireRole></RequireFeature>} />

          {/* Opérations — prise d'ordres & suivi (module optionnel + admin/editor) */}
          <Route path="/operations" element={<RequireFeature feature="operations"><RequireRole roles={['admin', 'editor']}><OperationsConsolePage /></RequireRole></RequireFeature>} />
          <Route path="/operations/referentiels" element={<RequireFeature feature="operations"><RequireRole roles={['admin', 'editor']}><BaseOperationsPage /></RequireRole></RequireFeature>} />
          {/* Les lieux se gèrent dans la console Data : l'ancienne page n'existe
              plus, le lien reste valable pour les favoris et les liens envoyés. */}
          <Route path="/operations/locations" element={<Navigate to="/data?table=lieux" replace />} />
          <Route path="/operations/transporteurs" element={<RequireFeature feature="operations"><RequireRole roles={['admin', 'editor']}><OperationsCarriersPage /></RequireRole></RequireFeature>} />
          <Route path="/robert" element={<RequireFeature feature="robert"><RobertPage /></RequireFeature>} />
          <Route path="/energy-indexes" element={<Navigate to="/settings/energy" replace />} />

          {/* Info — read-only fuel index curves */}
          <Route path="/info/fuel-indices" element={<FuelIndicesInfoPage />} />

          {/* Console Data — tables, imports (datasets) et connexions (editor+) */}
          <Route path="/data" element={<RequireRole roles={['admin', 'editor']}><DataConsolePage /></RequireRole>} />

          {/* Budget & Analyse — coûts réels, resimulations par tag tarifaire, choix transporteurs */}
          <Route path="/analysis" element={<RequireFeature feature="budget"><RequireRole roles={['admin', 'editor']}><BudgetAnalysisPage /></RequireRole></RequireFeature>} />

          {/* Datasets d'ordres transport — référentiel central AO/budget/analyse (editor+) */}
          <Route path="/datasets" element={<RequireRole roles={['admin', 'editor']}><DatasetsListPage /></RequireRole>} />
          {/* Zones tarifaires géographiques — onglet dédié de la console Data,
              éditeur en page pleine (le tracé et son récap ne tiennent pas
              dans une modale). */}
          <Route path="/zones" element={<Navigate to="/data?tab=zones" replace />} />
          <Route path="/zones/new" element={<RequireRole roles={['admin', 'editor']}><ZoneEditorPage /></RequireRole>} />
          <Route path="/zones/:id" element={<RequireRole roles={['admin', 'editor']}><ZoneEditorPage /></RequireRole>} />
          <Route path="/datasets/import/:id" element={<RequireRole roles={['admin', 'editor']}><DatasetImportPage /></RequireRole>} />
          <Route path="/datasets/:id" element={<RequireRole roles={['admin', 'editor']}><DatasetDetailPage /></RequireRole>} />

          {/* Import de grilles tarifaires multi-transporteurs (console Data) */}
          <Route path="/pricing-grid-imports/:id" element={<RequireRole roles={['admin', 'editor']}><PricingGridImportWizardPage /></RequireRole>} />

          {/* Imports (requires editor+) */}
          <Route path="/imports" element={<RequireRole roles={['admin', 'editor']}><ImportsTabPage /></RequireRole>} />
          <Route path="/imports/:id" element={<RequireRole roles={['admin', 'editor']}><GridImportDetailPage /></RequireRole>} />
          <Route path="/imports/ancillary/new" element={<RequireRole roles={['admin', 'editor']}><AncillaryImportPage /></RequireRole>} />
          <Route path="/ancillary-imports/:id" element={<RequireRole roles={['admin', 'editor']}><AncillaryImportPage /></RequireRole>} />

          {/* Carriers (requires editor+) */}
          <Route path="/carriers" element={<RequireRole roles={['admin', 'editor']}><CarriersRoute /></RequireRole>} />
          <Route path="/carriers/:id" element={<RequireRole roles={['admin', 'editor']}><CarrierDetailPage /></RequireRole>} />

          {/* Logistics sites (requires editor+) — same underlying entity, filtered by type */}
          <Route path="/logistics-sites" element={<RequireRole roles={['admin', 'editor']}><CarriersPage carrierTypeFilter="LOGISTICS" /></RequireRole>} />
          <Route path="/logistics-sites/:id" element={<RequireRole roles={['admin', 'editor']}><CarrierDetailPage /></RequireRole>} />

          {/* Tenders — appels d'offres (module optionnel + editor+) */}
          <Route path="/tenders" element={<RequireFeature feature="tenders"><RequireRole roles={['admin', 'editor']}><TendersListPage /></RequireRole></RequireFeature>} />
          <Route path="/tenders/:id" element={<RequireFeature feature="tenders"><RequireRole roles={['admin', 'editor']}><TenderDetailPage /></RequireRole></RequireFeature>} />
          <Route path="/tenders/:tenderId/proposals/:proposalId" element={<RequireFeature feature="tenders"><RequireRole roles={['admin', 'editor']}><TenderProposalDetailPage /></RequireRole></RequireFeature>} />

          {/* Vertical automotive / commissionnaire — détail d'un AO (module optionnel) */}
          <Route path="/auto-tenders/:id" element={<RequireFeature feature="autoTenders"><RequireRole roles={['admin', 'editor']}><AutoTenderDetailPage /></RequireRole></RequireFeature>} />
          <Route path="/auto-reference" element={<RequireFeature feature="autoTenders"><RequireRole roles={['admin', 'editor']}><AutoReferencePage /></RequireRole></RequireFeature>} />
          <Route path="/carrier-pool" element={<CarrierPoolRedirect />} />
          <Route path="/carrier-pool/:id" element={<RequireFeature feature="carrierPool"><RequireRole roles={['admin', 'editor']}><CarrierPoolDetailPage /></RequireRole></RequireFeature>} />
          <Route path="/sourcing" element={<RequireFeature feature="sourcing"><RequireRole roles={['admin', 'editor']}><SourcingPage /></RequireRole></RequireFeature>} />

          {/* Donneurs d'ordres — référentiel partagé opérations + AO (editor+) */}
          <Route path="/ordering-parties" element={<RequireRole roles={['admin', 'editor']}><OrderingPartiesPage /></RequireRole>} />

          {/* Legacy /admin — remplacé par /settings */}
          <Route path="/admin" element={<Navigate to="/settings" replace />} />

          {/* Settings */}
          <Route path="/settings" element={<SettingsLayout />}>
            <Route index element={<SettingsIndexRedirect />} />
            <Route path="general" element={<RequireRole roles={['admin', 'editor']}><SettingsGeneralPage /></RequireRole>} />
            <Route path="team" element={<RequireRole roles={['admin']}><SettingsTeamPage /></RequireRole>} />
            <Route path="branding" element={<RequireRole roles={['admin', 'editor']}><SettingsBrandingPage /></RequireRole>} />
            <Route path="emails" element={<RequireRole roles={['admin', 'editor']}><SettingsEmailsPage /></RequireRole>} />
            <Route path="activity" element={<RequireRole roles={['admin', 'editor']}><SettingsActivityPage /></RequireRole>} />
            <Route path="referential" element={<RequireRole roles={['admin', 'editor']}><SettingsReferentialPage /></RequireRole>} />
            <Route path="zones" element={<RequireFeature feature="autoTenders"><RequireRole roles={['admin', 'editor']}><SettingsZonesPage /></RequireRole></RequireFeature>} />
            <Route path="data" element={<RequireRole roles={['admin']}><SettingsDataPage /></RequireRole>} />
 
            <Route path="grid-templates" element={<RequireRole roles={['admin', 'editor']}><SettingsGridTemplatesPage /></RequireRole>} />
            <Route path="maturity" element={<RequireRole roles={['admin']}><SettingsMaturityPage /></RequireRole>} />
            {/* Conformité documentaire des transporteurs (vigilance fournisseurs) */}
            <Route path="compliance" element={<RequireRole roles={['admin', 'editor']}><VigilanceComplianceListPage /></RequireRole>} />
            <Route path="compliance/:spaceId" element={<RequireRole roles={['admin', 'editor']}><VigilanceSpaceDetailPage /></RequireRole>} />
            <Route path="energy" element={<SettingsEnergyPage />} />
            <Route path="account" element={<SettingsAccountPage />} />
          </Route>

          {/* Super Admin (restricted) */}
          <Route
            path="/superadmin"
            element={
              <RequireRole roles={['superadmin']}>
                <SuperAdminPage />
              </RequireRole>
            }
          />

          {/* Redirects for old routes */}
          <Route path="/service-presets" element={<Navigate to="/settings/referential" replace />} />
          <Route path="/import" element={<Navigate to="/imports" replace />} />

          {/* Catch-all connecté : la 404 se rend DANS le Layout — l'utilisateur
              garde la sidebar et un chemin de sortie vers l'accueil. */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Catch-all hors session (lien mort reçu sans être connecté). */}
        <Route path="*" element={<NotFound standalone />} />
      </Routes>
    </Suspense>
  );
}

function NotFound({ standalone = false }: { standalone?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className={`flex items-center justify-center p-6 text-center ${standalone ? 'min-h-screen' : 'min-h-[60vh]'}`}>
      <div>
        <div className="text-lg font-semibold text-text">{t('notFound.title')}</div>
        <p className="mt-1 text-sm text-muted">{t('notFound.description')}</p>
        <a href="/" className="mt-4 inline-block rounded bg-accent px-4 py-2 text-sm font-medium text-white">
          {t('notFound.backHome')}
        </a>
      </div>
    </div>
  );
}
